import React, { useState, useEffect, useRef } from 'react';
import {
  HomeModernIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient, { exportAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { marked } from 'marked';

// Types
interface Country {
  code: string;
  name: string;
  regions_label: string;
}

interface Region {
  code: string;
  name: string;
}

interface RightsSection {
  title: string;
  items: Array<{
    label: string;
    value: string | string[];
  }>;
}

interface Resource {
  name: string;
  url: string;
}

interface RightsGuide {
  country: string;
  country_name: string;
  region: string;
  region_name: string;
  sections: RightsSection[];
  resources?: Resource[];
}

interface LeaseIssue {
  id: string;
  name: string;
  description: string;
  severity: string;
  matched_language: string;
  recommendation: string;
  jurisdiction_note?: string;
}

interface ComplianceNote {
  category: string;
  requirement: string;
  description: string;
}

interface LeaseAnalysis {
  country: string;
  region: string;
  region_name: string;
  analysis_summary: {
    total_issues: number;
    total_warnings: number;
    high_severity_count: number;
    medium_severity_count: number;
    low_severity_count: number;
  };
  issues: LeaseIssue[];
  warnings: LeaseIssue[];
  compliance_notes: ComplianceNote[];
  recommendation: string;
}

interface IssueType {
  id: string;
  name: string;
  description: string;
  subcategories: string[];
}

interface IssueCheckResult {
  country: string;
  region: string;
  region_name: string;
  issue_type: string;
  issue_name: string;
  your_rights: string[];
  recommended_actions: string[];
  relevant_laws: string[];
  resources: Resource[];
  disclaimer: string;
}

interface LetterResponse {
  letter: string;
  subject: string;
  issue_type: string;
  issue_name: string;
  tips: string[];
}

// Step definitions
const STEPS = ['jurisdiction', 'knowRights', 'analyzeLease', 'checkIssue', 'generateLetter'];

export const TenantRights: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Current step
  const [currentStep, setCurrentStep] = useState(0);

  // Jurisdiction selection
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [regionsLabel, setRegionsLabel] = useState('State');

  // Rights guide
  const [rightsGuide, setRightsGuide] = useState<RightsGuide | null>(null);

  // Lease analysis
  const [leaseText, setLeaseText] = useState('');
  const [leaseFile, setLeaseFile] = useState<File | null>(null);
  const [leaseAnalysis, setLeaseAnalysis] = useState<LeaseAnalysis | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Issue check
  const [issueTypes, setIssueTypes] = useState<IssueType[]>([]);
  const [selectedIssueType, setSelectedIssueType] = useState('');
  const [issueDetails, setIssueDetails] = useState('');
  const [issueCheckResult, setIssueCheckResult] = useState<IssueCheckResult | null>(null);

  // Letter generation
  const [tenantName, setTenantName] = useState('');
  const [tenantAddress, setTenantAddress] = useState('');
  const [landlordName, setLandlordName] = useState('');
  const [landlordAddress, setLandlordAddress] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [letterIssueType, setLetterIssueType] = useState('');
  const [letterIssueDetails, setLetterIssueDetails] = useState('');
  const [letterResponse, setLetterResponse] = useState<LetterResponse | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadCountries();
    loadIssueTypes();
  }, []);

  // Load regions when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadRegions();
      setSelectedRegion('');
      setRightsGuide(null);
    }
  }, [selectedCountry]);

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/tenant-rights/countries');
      setCountries(response.data.countries);
    } catch (err) {
      setError(t('tenantRights:errors.loadCountries', 'Failed to load countries'));
    }
  };

  const loadRegions = async () => {
    try {
      const response = await apiClient.get(`/tenant-rights/regions/${selectedCountry}`);
      setRegions(response.data.regions);
      setRegionsLabel(response.data.regions_label);
    } catch (err) {
      setError(t('tenantRights:errors.loadRegions', 'Failed to load regions'));
    }
  };

  const loadIssueTypes = async () => {
    try {
      const response = await apiClient.get('/tenant-rights/issue-types');
      setIssueTypes(response.data.issue_types);
    } catch (err) {
      console.error('Failed to load issue types:', err);
    }
  };

  const loadRightsGuide = async () => {
    if (!selectedCountry || !selectedRegion) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/tenant-rights/rights-guide/${selectedCountry}/${selectedRegion}`);
      setRightsGuide(response.data);
    } catch (err) {
      setError(t('tenantRights:errors.loadRules', 'Failed to load tenant rights'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
      if (validTypes.includes(file.type) || file.name.endsWith('.pdf') || file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
        setLeaseFile(file);
        setLeaseText(''); // Clear text when file is selected
      } else {
        setError(t('tenantRights:errors.invalidFileType', 'Please upload a PDF or Word document'));
      }
    }
  };

  const clearFile = () => {
    setLeaseFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const analyzeLease = async () => {
    if (!leaseText.trim() && !leaseFile) return;

    setIsLoading(true);
    setError(null);

    try {
      let response;
      if (leaseFile) {
        // Upload file
        const formData = new FormData();
        formData.append('file', leaseFile);
        formData.append('country', selectedCountry);
        formData.append('region', selectedRegion);
        formData.append('language', i18n.language);
        response = await apiClient.post('/tenant-rights/analyze-lease-file', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use text
        response = await apiClient.post('/tenant-rights/analyze-lease', {
          country: selectedCountry,
          region: selectedRegion,
          lease_text: leaseText,
          language: i18n.language
        });
      }
      setLeaseAnalysis(response.data);
    } catch (err) {
      setError(t('tenantRights:errors.analyzeLease', 'Failed to analyze lease'));
    } finally {
      setIsLoading(false);
    }
  };

  const checkIssue = async () => {
    if (!selectedIssueType || !issueDetails.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/tenant-rights/check-issue', {
        country: selectedCountry,
        region: selectedRegion,
        issue_type: selectedIssueType,
        issue_details: issueDetails,
        language: i18n.language
      });
      setIssueCheckResult(response.data);
    } catch (err) {
      setError(t('tenantRights:errors.checkIssue', 'Failed to check issue'));
    } finally {
      setIsLoading(false);
    }
  };

  const generateLetter = async () => {
    if (!tenantName || !landlordName || !propertyAddress || !letterIssueType || !letterIssueDetails) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/tenant-rights/generate-letter', {
        country: selectedCountry,
        region: selectedRegion,
        issue_type: letterIssueType,
        issue_details: letterIssueDetails,
        landlord_name: landlordName,
        landlord_address: landlordAddress,
        tenant_name: tenantName,
        tenant_address: tenantAddress,
        property_address: propertyAddress,
        language: i18n.language
      });
      setLetterResponse(response.data);
    } catch (err) {
      setError(t('tenantRights:errors.generateLetter', 'Failed to generate letter'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    if (!letterResponse?.letter) return;
    await exportDocument(format);
  };

  const printLetter = () => {
    if (!letterResponse?.letter) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const issueTypeName = issueTypes.find(t => t.id === letterResponse.issue_type)?.name || letterResponse.issue_name;
    const htmlContent = marked(letterResponse.letter, { async: false }) as string;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tenant Letter - ${issueTypeName}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; margin-top: 24px; }
            h2 { font-size: 20px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; }
            h3 { font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; }
            h4 { font-size: 16px; font-weight: 600; margin-top: 12px; margin-bottom: 8px; }
            p { margin-bottom: 12px; line-height: 1.6; }
            ul, ol { margin-left: 24px; margin-bottom: 12px; }
            li { margin-bottom: 4px; line-height: 1.6; }
            strong { font-weight: 600; color: #111827; }
            em { font-style: italic; }
            code { background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; }
            pre { background-color: #1f2937; color: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; }
            blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; margin: 12px 0; color: #374151; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="content">${htmlContent}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportDocument = async (format: 'word' | 'text' | 'pdf') => {
    if (!letterResponse?.letter) return;

    try {
      const issueTypeName = issueTypes.find(t => t.id === letterResponse.issue_type)?.name || letterResponse.issue_name;
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `tenant-letter-${issueTypeName.toLowerCase().replace(/\s+/g, '-')}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content: letterResponse.letter,
        title: `Tenant Letter - ${issueTypeName}`,
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      // Try to use File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            return;
          }
        }
      }

      // Fallback: Standard download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('tenantRights:errors.exportFailed', 'Failed to export document. Please try again.'));
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // jurisdiction
        return selectedCountry && selectedRegion;
      default:
        return true;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800';
      case 'low':
        return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600';
    }
  };

  const renderJurisdiction = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('tenantRights:jurisdiction.selectCountry', 'Select Country')}
          </label>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('tenantRights:jurisdiction.selectCountryPrompt', 'Select a country...')}</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>

        {selectedCountry && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tenantRights:jurisdiction.selectRegion', 'Select {{regionLabel}}', { regionLabel: regionsLabel })}
            </label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('tenantRights:jurisdiction.selectRegionPrompt', 'Select a {{regionLabel}}...', { regionLabel: regionsLabel.toLowerCase() })}</option>
              {regions.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedCountry && selectedRegion && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-green-800 dark:text-green-200">
              {t('tenantRights:jurisdiction.selectedLocation', 'Selected Location')}: {regions.find(r => r.code === selectedRegion)?.name}, {countries.find(c => c.code === selectedCountry)?.name}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const renderKnowRights = () => (
    <div className="space-y-6">
      <button
        onClick={loadRightsGuide}
        disabled={isLoading}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('tenantRights:common.loading', 'Loading...') : t('tenantRights:rights.loadButton', 'View My Rights')}
      </button>

      {rightsGuide && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('tenantRights:rights.title', 'Your Tenant Rights in {{location}}', { location: rightsGuide.region_name })}
          </h3>

          {rightsGuide.sections.map((section, idx) => (
            <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">{section.title}</h4>
              <div className="space-y-2">
                {section.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="flex flex-col sm:flex-row sm:justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{item.label}:</span>
                    {Array.isArray(item.value) ? (
                      <ul className="list-disc list-inside text-sm text-gray-900 dark:text-white ml-4 sm:ml-0 sm:text-right">
                        {item.value.slice(0, 5).map((v, vIdx) => (
                          <li key={vIdx}>{v}</li>
                        ))}
                        {item.value.length > 5 && <li>...and {item.value.length - 5} more</li>}
                      </ul>
                    ) : (
                      <span className="text-sm text-gray-900 dark:text-white">{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {rightsGuide.resources && rightsGuide.resources.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">
                {t('tenantRights:rights.resources', 'Helpful Resources')}
              </h4>
              <div className="space-y-2">
                {rightsGuide.resources.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {resource.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAnalyzeLease = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('tenantRights:lease.description', 'Upload a lease document or paste text below to check for potentially problematic or illegal clauses.')}
      </p>

      {/* File Upload Section */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('tenantRights:lease.uploadLabel', 'Upload Document')}
        </label>
        <div className="flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileSelect}
            className="hidden"
            id="lease-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowUpTrayIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <span className="text-gray-600 dark:text-gray-300">
              {t('tenantRights:lease.selectFile', 'Select PDF or Word file')}
            </span>
          </button>
          {leaseFile && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <DocumentTextIcon className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-blue-700 dark:text-blue-300 max-w-[200px] truncate">
                {leaseFile.name}
              </span>
              <button
                onClick={clearFile}
                className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded"
              >
                <XMarkIcon className="h-4 w-4 text-blue-500" />
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('tenantRights:lease.supportedFormats', 'Supported formats: PDF, Word (.doc, .docx)')}
        </p>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            {t('tenantRights:lease.or', 'or paste text below')}
          </span>
        </div>
      </div>

      {/* Text Input Section */}
      <div>
        <textarea
          value={leaseText}
          onChange={(e) => {
            setLeaseText(e.target.value);
            if (e.target.value) {
              clearFile(); // Clear file when text is entered
            }
          }}
          placeholder={t('tenantRights:lease.placeholder', 'Paste your lease agreement text here...')}
          rows={10}
          disabled={!!leaseFile}
          className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${leaseFile ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
      </div>

      <button
        onClick={analyzeLease}
        disabled={isLoading || (!leaseText.trim() && !leaseFile)}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('tenantRights:common.analyzing', 'Analyzing...') : t('tenantRights:lease.analyzeButton', 'Analyze Lease')}
      </button>

      {leaseAnalysis && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">
              {t('tenantRights:lease.summary', 'Analysis Summary')}
            </h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className={`p-3 rounded-lg ${leaseAnalysis.analysis_summary.high_severity_count > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-gray-600'}`}>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{leaseAnalysis.analysis_summary.high_severity_count}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{t('tenantRights:lease.severity.high', 'High')}</div>
              </div>
              <div className={`p-3 rounded-lg ${leaseAnalysis.analysis_summary.medium_severity_count > 0 ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-100 dark:bg-gray-600'}`}>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{leaseAnalysis.analysis_summary.medium_severity_count}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{t('tenantRights:lease.severity.medium', 'Medium')}</div>
              </div>
              <div className={`p-3 rounded-lg ${leaseAnalysis.analysis_summary.low_severity_count > 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-600'}`}>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{leaseAnalysis.analysis_summary.low_severity_count}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{t('tenantRights:lease.severity.low', 'Low')}</div>
              </div>
            </div>
          </div>

          {/* Issues */}
          {leaseAnalysis.issues.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                {t('tenantRights:lease.issues', 'Issues Found')}
              </h4>
              {leaseAnalysis.issues.map((issue, idx) => (
                <div key={idx} className={`p-4 border rounded-lg ${getSeverityColor(issue.severity)}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium">{issue.name}</span>
                    <span className="text-xs uppercase font-bold px-2 py-1 rounded">{issue.severity}</span>
                  </div>
                  <p className="text-sm mb-2">{issue.description}</p>
                  <p className="text-sm italic">"{issue.matched_language}"</p>
                  <p className="text-sm mt-2">{issue.recommendation}</p>
                  {issue.jurisdiction_note && (
                    <p className="text-sm mt-1 font-medium">{issue.jurisdiction_note}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {leaseAnalysis.warnings.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2" />
                {t('tenantRights:lease.warnings', 'Warnings')}
              </h4>
              {leaseAnalysis.warnings.map((warning, idx) => (
                <div key={idx} className={`p-4 border rounded-lg ${getSeverityColor(warning.severity)}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium">{warning.name}</span>
                    <span className="text-xs uppercase font-bold px-2 py-1 rounded">{warning.severity}</span>
                  </div>
                  <p className="text-sm mb-2">{warning.description}</p>
                  <p className="text-sm">{warning.recommendation}</p>
                </div>
              ))}
            </div>
          )}

          {/* Compliance Notes */}
          {leaseAnalysis.compliance_notes.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2" />
                {t('tenantRights:lease.complianceNotes', 'Compliance Notes')}
              </h4>
              {leaseAnalysis.compliance_notes.map((note, idx) => (
                <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <span className="font-medium text-blue-900 dark:text-blue-100">{note.requirement}</span>
                  {note.description && <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">{note.description}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Recommendation */}
          <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">
              {t('tenantRights:lease.recommendation', 'Recommendation')}
            </h4>
            <p className="text-sm text-green-800 dark:text-green-200">{leaseAnalysis.recommendation}</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderCheckIssue = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('tenantRights:issue.description', 'Select the type of issue you\'re experiencing and describe the details.')}
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('tenantRights:issue.selectType', 'Type of Issue')}
        </label>
        <select
          value={selectedIssueType}
          onChange={(e) => setSelectedIssueType(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">{t('tenantRights:issue.selectTypePrompt', 'Select issue type...')}</option>
          {issueTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {t(`tenantRights:issue.types.${type.id}`, type.name)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('tenantRights:issue.details', 'Describe Your Situation')}
        </label>
        <textarea
          value={issueDetails}
          onChange={(e) => setIssueDetails(e.target.value)}
          placeholder={t('tenantRights:issue.detailsPlaceholder', 'Provide details about your issue...')}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      <button
        onClick={checkIssue}
        disabled={isLoading || !selectedIssueType || !issueDetails.trim()}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('tenantRights:common.checking', 'Checking...') : t('tenantRights:issue.checkButton', 'Check My Rights')}
      </button>

      {issueCheckResult && (
        <div className="space-y-6">
          <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 className="font-medium text-green-900 dark:text-green-100 mb-3">
              {t('tenantRights:issue.yourRights', 'Your Rights')}
            </h4>
            <ul className="list-disc list-inside text-sm text-green-800 dark:text-green-200 space-y-1">
              {issueCheckResult.your_rights.map((right, idx) => (
                <li key={idx}>{right}</li>
              ))}
            </ul>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">
              {t('tenantRights:issue.recommendedActions', 'Recommended Actions')}
            </h4>
            <ol className="list-decimal list-inside text-sm text-blue-800 dark:text-blue-200 space-y-1">
              {issueCheckResult.recommended_actions.map((action, idx) => (
                <li key={idx}>{action}</li>
              ))}
            </ol>
          </div>

          {issueCheckResult.resources.length > 0 && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                {t('tenantRights:issue.resources', 'Resources')}
              </h4>
              <div className="space-y-2">
                {issueCheckResult.resources.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {resource.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderGenerateLetter = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('tenantRights:letter.description', 'Create a formal letter to your landlord addressing the issue.')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('tenantRights:letter.tenantName', 'Your Name')}
          </label>
          <input
            type="text"
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('tenantRights:letter.landlordName', "Landlord's Name")}
          </label>
          <input
            type="text"
            value={landlordName}
            onChange={(e) => setLandlordName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('tenantRights:letter.tenantAddress', 'Your Address')}
          </label>
          <input
            type="text"
            value={tenantAddress}
            onChange={(e) => setTenantAddress(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('tenantRights:letter.landlordAddress', "Landlord's Address")}
          </label>
          <input
            type="text"
            value={landlordAddress}
            onChange={(e) => setLandlordAddress(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('tenantRights:letter.propertyAddress', 'Property Address')}
          </label>
          <input
            type="text"
            value={propertyAddress}
            onChange={(e) => setPropertyAddress(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('tenantRights:letter.issueType', 'Issue Type')}
          </label>
          <select
            value={letterIssueType}
            onChange={(e) => setLetterIssueType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('tenantRights:issue.selectTypePrompt', 'Select issue type...')}</option>
            {issueTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {t(`tenantRights:issue.types.${type.id}`, type.name)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('tenantRights:letter.issueDetails', 'Issue Details')}
        </label>
        <textarea
          value={letterIssueDetails}
          onChange={(e) => setLetterIssueDetails(e.target.value)}
          placeholder={t('tenantRights:letter.issueDetailsPlaceholder', 'Describe the issue in detail...')}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      <button
        onClick={generateLetter}
        disabled={isLoading || !tenantName || !landlordName || !propertyAddress || !letterIssueType || !letterIssueDetails}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('tenantRights:common.generating', 'Generating...') : t('tenantRights:letter.generateButton', 'Generate Letter')}
      </button>

      {letterResponse && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-medium text-gray-900 dark:text-white">
              {t('tenantRights:letter.preview', 'Letter Preview')}
            </h4>
            <ExportDropdownMenu
              onExport={handleExport}
              onPrint={printLetter}
              showPrint={true}
            />
          </div>

          <div className="p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
              {letterResponse.letter}
            </pre>
          </div>

          {letterResponse.tips.length > 0 && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                {t('tenantRights:letter.tips', 'Important Tips')}
              </h4>
              <ul className="list-disc list-inside text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
                {letterResponse.tips.map((tip, idx) => (
                  <li key={idx}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderJurisdiction();
      case 1:
        return renderKnowRights();
      case 2:
        return renderAnalyzeLease();
      case 3:
        return renderCheckIssue();
      case 4:
        return renderGenerateLetter();
      default:
        return null;
    }
  };

  const stepIcons = [
    HomeModernIcon,
    ShieldCheckIcon,
    DocumentTextIcon,
    ClipboardDocumentListIcon,
    EnvelopeIcon,
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      {/* Loading Overlay for processing */}
      <LoadingOverlay
        isVisible={isLoading}
        message={t('tenantRights:loading.processing', 'Processing...')}
        subMessage={t('tenantRights:loading.pleaseWait', 'Analyzing your request')}
      />

      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <HomeModernIcon className="h-12 w-12 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('tenantRights:title', 'Tenant Rights Checker')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('tenantRights:subtitle', 'Understand your rights as a tenant and analyze lease agreements')}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const Icon = stepIcons[index];
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => index <= currentStep && setCurrentStep(index)}
                      disabled={index > currentStep && !canProceed()}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isActive
                          ? 'bg-blue-600 dark:bg-blue-500 text-white'
                          : isCompleted
                          ? 'bg-green-500 text-white cursor-pointer'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      } ${index <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon className="h-6 w-6" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </button>
                    <span
                      className={`text-xs mt-2 hidden sm:block ${
                        isActive ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {t(`tenantRights:steps.${step}`, step)}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded ${
                        index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            {t(`tenantRights:stepTitles.${STEPS[currentStep]}`, STEPS[currentStep])}
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="flex items-center px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('tenantRights:navigation.back', 'Back')}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={() => setCurrentStep(Math.min(STEPS.length - 1, currentStep + 1))}
              disabled={!canProceed()}
              className="flex items-center px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('tenantRights:navigation.next', 'Next')}
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={() => setCurrentStep(0)}
              className="flex items-center px-6 py-3 bg-green-600 dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
            >
              {t('tenantRights:navigation.done', 'Done')}
            </button>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                {t('tenantRights:disclaimer.title', 'Legal Disclaimer')}
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                {t('tenantRights:disclaimer.text', 'This tool provides general information about tenant rights and is for educational purposes only. It does not constitute legal advice. Laws vary by jurisdiction and change over time. Always verify information with local authorities and consider consulting with a licensed attorney for advice specific to your situation.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
